import type { ThemeDefinition } from 'vuetify';

type ThemeVariables = NonNullable<ThemeDefinition['variables']>;

/**
 * Opacity system — the single home for the alphas the theme exposes as
 * `--v-*` custom properties. Consume as the alpha argument of rgba(), e.g.
 *   rgba(var(--v-theme-warning), var(--v-opacity-darker))
 *
 * The split mirrors how the themes consume them:
 *  - sharedOpacity: identical in light + dark
 *  - lightOpacity / darkOpacity: differ (dark alphas are higher to hold
 *    perceptual weight on dark surfaces)
 *
 * Colour-overlay scale (`opacity-softer … opacity-darker`) — the Figma
 * "opacity color/{theme}/*" tiers. The base colour is the colour's `main`
 * (root) token; the tier name is the overlay STRENGTH (alpha), NOT a
 * darker/lighter base shade — so `--v-opacity-darker` is the strongest overlay:
 *   rgba(var(--v-theme-success), var(--v-opacity-darker))
 *
 * Shadow alphas (`shadow-*-opacity`) live with the elevation config in
 * index.ts, not here — they belong to a different system.
 */

// Same value in both themes
export const sharedOpacity: ThemeVariables = {
  'high-emphasis-opacity': 0.9,
  'medium-emphasis-opacity': 0.7,
  'hover-opacity': 0.06,
  'border-hover-opacity': 0.25,
  'action-hover': 0.06,
  'action-focus': 0.1
};

export const lightOpacity: ThemeVariables = {
  // Colour-overlay scale
  'opacity-softer': 0.04,
  'opacity-soft': 0.08,
  'opacity-main': 0.12,
  'opacity-dark': 0.14,
  'opacity-darker': 0.18,
  // State / interaction
  'border-opacity': 0.22,
  'disabled-opacity': 0.5,
  'activated-opacity': 0.08
};

export const darkOpacity: ThemeVariables = {
  // Colour-overlay scale
  'opacity-softer': 0.08,
  'opacity-soft': 0.18,
  'opacity-main': 0.22,
  'opacity-dark': 0.28,
  'opacity-darker': 0.36,
  // State / interaction
  'border-opacity': 0.24,
  'disabled-opacity': 0.55,
  'activated-opacity': 0.18
};
